import {useEffect, useRef, useState} from "react";

import {useSnapshot} from "valtio";

import {IValtioModel} from "@/shared/models/base-model.interface";

export function useModel<TModel extends IValtioModel, TArgs extends Array<unknown> = []>(
    factory: (...args: TArgs) => TModel,
    ...args: TArgs
): [model: TModel, snapshot: TModel] {
    // Store factory args for Strict Mode recreation
    const argsRef = useRef(args);
    const factoryRef = useRef(factory);

    // Create initial model
    const [model, setModel] = useState(() => factory(...args));
    const isInitialized = useRef(false);

    const snapshot = useSnapshot(model) as TModel;

    useEffect(() => {
        if (isInitialized.current) {
            const newModel = factoryRef.current(...argsRef.current);
            setModel(newModel);
            if (newModel.init) {
                void newModel.init();
            }
            return () => {
                if (newModel?.dispose) {
                    void newModel.dispose();
                }
            };
        }

        // First mount - initialize model
        isInitialized.current = true;
        if (model.init) {
            void model.init();
        }

        return () => {
            if (model?.dispose) {
                void model.dispose();
            }
        };
    }, []); // Empty deps - run only on mount/unmount

    return [model, snapshot];
}
